import React, { useEffect, useState } from "react";
import { FaArrowRight, FaParking, FaMapMarkerAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import fetchLandingProducts from "../../common/libs/fetchLandingProducts";

const createSlug = (name) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const ProductCard = ({ product, accent }) => {
  const name     = product?.name || "N/A";
  const image    = product?.image?.src || product?.image_url || product?.image;
  const type     = product?.display_name || product?.type_name || "N/A";
  const terminals = Array.isArray(product?.terminals)
    ? product.terminals.map((t) => t?.name).filter(Boolean).join(" · ")
    : "N/A";
  const slug = product?.slug || createSlug(name);

  const accentBg  = accent === "primary" ? "bg-primary"     : "bg-lightprimary";
  const accentBtn = accent === "primary"
    ? "bg-primary hover:bg-lightprimary"
    : "bg-lightprimary hover:bg-primary";

  return (
    <Link
      to={`/luton-airport-parking/${slug}`}
      className="flex flex-col bg-white overflow-hidden border border-primary/10 shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group"
      style={{ borderRadius: "0 1.5rem 0 1.5rem" }}
    >
      <div className="relative w-full h-44 bg-primary/5 flex items-center justify-center overflow-hidden">
        {image ? (
          <img src={image} alt={name}
            className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <FaParking className="text-6xl text-primary/20" />
        )}
        <span className={`absolute top-3 left-3 px-3 py-1 text-xs font-black rounded-full ${accentBg} text-white shadow`}>
          {type}
        </span>
      </div>
      <div className="flex flex-col flex-1 p-4 gap-3">
        <h3 className="text-base font-black text-primary leading-snug line-clamp-2 group-hover:text-lightprimary transition-colors">{name}</h3>
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${accentBg}`} />
          <span className="text-xs font-bold text-primary/60 uppercase tracking-wide">{type}</span>
        </div>
        <div className="flex items-start gap-2">
          <FaMapMarkerAlt className="text-lightprimary text-sm flex-shrink-0 mt-0.5" />
          <span className="text-xs text-primary/70 font-semibold leading-snug">{terminals}</span>
        </div>
        <div className={`mt-auto flex items-center justify-between px-4 py-2.5 ${accentBtn} text-white transition-colors duration-300`} style={{ borderRadius: "0 0.75rem 0 0.75rem" }}>
          <span className="text-sm font-black">Book Now</span>
          <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
};

const SectionBlock = ({ title, list, accent, tagBg, tagText }) => (
  <div>
    <div className="flex items-center gap-3 mb-6">
      <span className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-black ${tagBg} ${tagText}`}>
        <FaParking /> {title}
      </span>
      <div className="flex-1 h-px bg-primary/10" />
      <span className="text-xs font-semibold text-primary/40">{list.length} option{list.length !== 1 ? "s" : ""}</span>
    </div>
    <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {list.map((product, idx) => (
        <ProductCard key={idx} product={product} accent={accent} />
      ))}
    </div>
  </div>
);

const ParkingTable = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchLandingProducts("LTN", "");
        if (res?.success && Array.isArray(res.data)) setProducts(res.data);
      } catch (e) { console.error("ParkingTable error", e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <div className="w-10 h-10 border-4 border-primary border-t-secondary rounded-full animate-spin" />
      <p className="text-sm font-bold text-primary/50">Loading parking options...</p>
    </div>
  );

  const parkAndRide = [], meetAndGreet = [];
  products.forEach((p) => {
    const label = (p?.display_name || p?.type_name || "").toLowerCase();
    if (label.includes("meet") && label.includes("greet")) meetAndGreet.push(p);
    else if (label.includes("park") && label.includes("ride")) parkAndRide.push(p);
  });

  if (!parkAndRide.length && !meetAndGreet.length) return (
    <div className="py-14 text-center">
      <FaParking className="mx-auto mb-3 text-5xl text-primary/20" />
      <p className="text-sm font-bold text-primary/40">No parking options available at the moment.</p>
    </div>
  );

  return (
    <div className="space-y-10 p-4 md:p-6 bg-white rounded-2xl">
      {parkAndRide.length > 0 && <SectionBlock title="Park & Ride" list={parkAndRide} accent="primary" tagBg="bg-secondary" tagText="text-primary" />}
      {meetAndGreet.length > 0 && <SectionBlock title="Meet & Greet" list={meetAndGreet} accent="lightprimary" tagBg="bg-lightprimary" tagText="text-white" />}
    </div>
  );
};

export default ParkingTable;
